// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { createTheme } from '@mui/material/styles';
import { TextTransformProperty } from 'csstype'; // eslint-disable-line import/no-unresolved

const navy = '#1c364b';
const lightOrangeColor = '#faa738';
const darkGrey = '#4f5a64';
const lightGrey = '#abb6c1';
// The dashboard's hairline border (Figma spec). On the palette so a styles file reads it off
// the theme it already receives; also exported below for callers outside MUI's palette shape.
const borderGrey = '#ebebed';
const lightShadow = '0px 2px 12px 0px rgba(0,0,0,0.1)';
const sidebarWidth = 350;
const lightBlue = '#01a1dd';

const blueColor = '#01A1DD';
const cyanColor = '#56E4F9';

const typography = {
  fontFamily: [
    '"Work Sans"',
    '"Roboto"',
    '"Helvetica"',
    '"Arial"',
    'sans-serif',
  ].join(','),
  h1: {
    fontSize: '30px',
    fontWeight: 300,
    letterSpacing: '1.3px',
    lineHeight: '35px',
  },
  h2: {
    fontSize: 20,
    fontWeight: 600,
    letterSpacing: '.86px',
    lineHeight: '24px',
  },
  h3: {
    fontSize: 18,
    fontWeight: 400,
    letterSpacing: '.75px',
    lineHeight: '24px',
  },
  h4: {
    fontSize: 16,
    fontWeight: 500,
    letterSpacing: '.2px',
    lineHeight: '19px',
    textTransform: 'uppercase' as TextTransformProperty,
    color: darkGrey,
  },
  h5: {
    fontSize: 16,
    fontWeight: 500,
    letterSpacing: '.7px',
    lineHeight: '19px',
  },
  h6: {
    fontSize: '14px',
    fontWeight: 500,
    letterSpacing: '.6px',
    lineHeight: '18px',
    textTransform: 'uppercase' as TextTransformProperty,
  },
  body1: {
    fontWeight: 300,
    color: darkGrey,
    letterSpacing: 'normal',
  },
  body2: {
    fontWeight: 300,
    color: '#000',
    fontSize: '12px',
    lineHeight: 1.3,
    letterSpacing: 'normal',
  },
};

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: blueColor,
    },
    secondary: {
      main: cyanColor,
      contrastText: '#FFFFFF',
    },
    text: {
      primary: navy,
    },
    lightGrey,
    darkGrey,
    borderGrey,
  },
  typography,
  lightShadow,
  sidebarWidth,
  components: {
    MuiButton: {
      styleOverrides: {
        contained: {
          borderRadius: 0,
          boxShadow: 'none',
        },
        outlined: {
          borderRadius: 0,
          borderColor: navy,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        elevation1: {
          boxShadow: lightShadow,
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        colorPrimary: {
          color: darkGrey,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontWeight: 400,
        },
      },
    },
    MuiTable: {
      responsiveStacked: {
        overflowX: 'inherit',
        overflowY: 'inherit',
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '1em',
        },
      },
    },
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#FFFFFF',
    },
    secondary: {
      main: lightBlue,
    },
    background: {
      default: '#2d2d2d',
      paper: navy,
    },
    borderGrey,
  },
  typography,
  components: {
    MuiPaper: {
      styleOverrides: {
        elevation1: {
          boxShadow: lightShadow,
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontWeight: 400,
        },
      },
    },
  },
});

const InputBaseWidth = createTheme({
  components: {
    MuiInput: {
      styleOverrides: {
        root: {
          width: '100%',
          zIndex: 0,
        },
      },
    },

  },
});

// The AW accent orange, for callers outside MUI's palette shape (map symbols, date emphasis).
export const awOrange = lightOrangeColor;

// Rail palette from the Figma dashboard spec (the file binds no variables, so these are its
// raw values promoted to tokens): panel ground, hairline borders, muted secondary text.
export const awPanelGrey = '#fafafc';
export const awBorderGrey = borderGrey;
export const awMutedGrey = '#788898';
// The Projects / Map switch from the Figma "Proposed — Projects list" spec: its grey track and
// the lift under the active segment.
export const awSwitchTrackGrey = '#e6ebee';
export const awSwitchLiftShadow = '0px 1px 1.5px rgba(21, 59, 90, 0.16)';
// The green the dashboard uses for work already done: completed pipeline steps on the card's
// stepper, filled segments on the row and rail meters.
export const awProgressGreen = '#08D38F';

export { lightTheme, darkTheme, InputBaseWidth };
